// Pure audio-clip editing + playback scheduling (no DOM): everything the editor does to clips is testable here.
import type { AudioClip, AudioRole, Project } from "../../../engine/src/types.ts";
import { uid } from "./ops.ts";

export const MIN_CLIP_MS = 50;
export const MAX_CLIPS = 32;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const withAudio = (p: Project, audio: AudioClip[]): Project => ({ ...p, audio });

export interface AssetLike { id: string; duration_ms: number; name: string }

/** New clip at `startMs` playing the whole asset, shortened to fit the project. null if there is no room. */
export function addClip(p: Project, asset: AssetLike, startMs: number, role: AudioRole = "sfx"): { project: Project; clipId: string } | null {
  if (p.audio.length >= MAX_CLIPS) return null;
  const start = clamp(Math.round(startMs), 0, Math.max(0, p.duration_ms - MIN_CLIP_MS));
  const dur = Math.min(asset.duration_ms, p.duration_ms - start);
  if (dur < MIN_CLIP_MS) return null;
  const clip: AudioClip = { id: uid("au"), asset_id: asset.id, name: asset.name.slice(0, 80), start_ms: start, in_ms: 0, duration_ms: dur,
    volume: 1, fade_in_ms: 0, fade_out_ms: 0, muted: false, role };
  return { project: withAudio(p, [...p.audio, clip]), clipId: clip.id };
}

export function removeClip(p: Project, id: string): Project {
  return p.audio.some((c) => c.id === id) ? withAudio(p, p.audio.filter((c) => c.id !== id)) : p;
}

/** Keep a clip valid: inside the project, inside its source, fades not longer than the clip. */
export function normalize(c: AudioClip, projectMs: number, sourceMs: number): AudioClip {
  const start = clamp(Math.round(c.start_ms), 0, Math.max(0, projectMs - MIN_CLIP_MS));
  const inMs = clamp(Math.round(c.in_ms), 0, Math.max(0, sourceMs - MIN_CLIP_MS));
  const dur = clamp(Math.round(c.duration_ms), MIN_CLIP_MS, Math.max(MIN_CLIP_MS, Math.min(projectMs - start, sourceMs - inMs)));
  const fi = clamp(Math.round(c.fade_in_ms), 0, dur);
  const fo = clamp(Math.round(c.fade_out_ms), 0, dur - fi);
  return { ...c, start_ms: start, in_ms: inMs, duration_ms: dur, fade_in_ms: fi, fade_out_ms: fo, volume: clamp(c.volume, 0, 2) };
}

export function updateClip(p: Project, id: string, patch: Partial<Omit<AudioClip, "id" | "asset_id">>, sourceMs: number): Project {
  const cur = p.audio.find((c) => c.id === id);
  if (!cur) return p;
  const next = normalize({ ...cur, ...patch }, p.duration_ms, sourceMs);
  return JSON.stringify(next) === JSON.stringify(cur) ? p : withAudio(p, p.audio.map((c) => (c.id === id ? next : c)));
}

/** Drag the left edge: the audible content stays glued to the timeline (start and in_ms move together). */
export function trimStart(p: Project, id: string, newStartMs: number, sourceMs: number): Project {
  const c = p.audio.find((x) => x.id === id);
  if (!c) return p;
  const end = c.start_ms + c.duration_ms;
  const lo = Math.max(0, c.start_ms - c.in_ms);                     // cannot reveal audio before the source begins
  const start = clamp(Math.round(newStartMs), lo, end - MIN_CLIP_MS);
  const delta = start - c.start_ms;
  return updateClip(p, id, { start_ms: start, in_ms: c.in_ms + delta, duration_ms: c.duration_ms - delta }, sourceMs);
}

/** Drag the right edge. */
export function trimEnd(p: Project, id: string, newEndMs: number, sourceMs: number): Project {
  const c = p.audio.find((x) => x.id === id);
  if (!c) return p;
  return updateClip(p, id, { duration_ms: Math.round(newEndMs) - c.start_ms }, sourceMs);
}

/** Split into two clips at a project time strictly inside the clip. */
export function splitClip(p: Project, id: string, atMs: number): { project: Project; newId: string } | null {
  const c = p.audio.find((x) => x.id === id);
  if (!c || p.audio.length >= MAX_CLIPS) return null;
  const cut = Math.round(atMs) - c.start_ms;
  if (cut < MIN_CLIP_MS || c.duration_ms - cut < MIN_CLIP_MS) return null;
  const a: AudioClip = { ...c, duration_ms: cut, fade_out_ms: 0, fade_in_ms: Math.min(c.fade_in_ms, cut) };
  const b: AudioClip = { ...c, id: uid("au"), start_ms: c.start_ms + cut, in_ms: c.in_ms + cut, duration_ms: c.duration_ms - cut, fade_in_ms: 0,
    fade_out_ms: Math.min(c.fade_out_ms, c.duration_ms - cut) };
  const i = p.audio.findIndex((x) => x.id === id);
  const audio = [...p.audio]; audio.splice(i, 1, a, b);
  return { project: withAudio(p, audio), newId: b.id };
}

/** Gain (0..2) of a clip at a time local to the clip, including linear fades. */
export function gainAt(c: AudioClip, localMs: number): number {
  if (c.muted || localMs < 0 || localMs > c.duration_ms) return 0;
  let g = c.volume;
  if (c.fade_in_ms > 0 && localMs < c.fade_in_ms) g *= localMs / c.fade_in_ms;
  const toEnd = c.duration_ms - localMs;
  if (c.fade_out_ms > 0 && toEnd < c.fade_out_ms) g *= toEnd / c.fade_out_ms;
  return g;
}

export interface Playback { clip: AudioClip; delayMs: number; offsetMs: number; playMs: number }
/** What to start when playback begins (or resumes after a seek) at `timeMs`. */
export function scheduleAt(clips: AudioClip[], timeMs: number): Playback[] {
  const out: Playback[] = [];
  for (const clip of clips) {
    if (clip.muted || clip.volume <= 0) continue;
    const end = clip.start_ms + clip.duration_ms;
    if (end <= timeMs) continue;
    if (clip.start_ms >= timeMs) out.push({ clip, delayMs: clip.start_ms - timeMs, offsetMs: clip.in_ms, playMs: clip.duration_ms });
    else { const into = timeMs - clip.start_ms; out.push({ clip, delayMs: 0, offsetMs: clip.in_ms + into, playMs: clip.duration_ms - into }); }
  }
  return out;
}

/** Waveform columns for a clip's visible slice, downsampled to at most `cols` bars (0..1). */
export function waveformSlice(peaks: number[], sourceMs: number, inMs: number, durMs: number, cols: number): number[] {
  if (!peaks.length || sourceMs <= 0 || cols <= 0) return [];
  const per = peaks.length / sourceMs;
  const a = Math.max(0, Math.floor(inMs * per)), b = Math.min(peaks.length, Math.max(a + 1, Math.ceil((inMs + durMs) * per)));
  const n = Math.max(1, Math.min(cols, b - a)), out: number[] = [];
  for (let i = 0; i < n; i++) {
    const s = a + Math.floor((i * (b - a)) / n), e = a + Math.max(Math.floor(((i + 1) * (b - a)) / n), Math.floor((i * (b - a)) / n) + 1);
    let m = 0; for (let j = s; j < Math.min(e, b); j++) m = Math.max(m, peaks[j]!);
    out.push(m / 255);
  }
  return out;
}

// ---- automatic ducking: music is lowered while a voice clip plays. Same formula as api/modila_api/audio.py `duck_gain`. ----
export type Interval = [number, number];
export function voiceIntervals(audio: AudioClip[]): Interval[] {
  return audio.filter((c) => c.role === "voice" && !c.muted && c.volume > 0).map((c) => [c.start_ms, c.start_ms + c.duration_ms]);
}
export function duckGainAt(tMs: number, iv: Interval[], amountDb: number, attackMs: number, releaseMs: number): number {
  const g = 10 ** (-amountDb / 20);
  let depth = 0;
  for (const [a, b] of iv) {
    const up = attackMs > 0 ? (tMs - (a - attackMs)) / attackMs : tMs >= a ? 1 : 0;
    const down = releaseMs > 0 ? (b + releaseMs - tMs) / releaseMs : tMs <= b ? 1 : 0;
    depth = Math.max(depth, Math.min(1, Math.max(0, Math.min(up, down))));
  }
  return 1 - (1 - g) * depth;
}
export const isDucked = (p: Project, c: AudioClip): boolean => c.role === "music" && p.ducking.enabled && p.ducking.amount_db > 0 && voiceIntervals(p.audio).length > 0;

/** Gain breakpoints (clip-local ms, linear gain) for WebAudio: clip volume + fades + ducking, exact at the corners and sampled
 *  every 40 ms where ducking curves intersect. Linear ramps between them reproduce the export's mix. */
export function envelope(p: Project, c: AudioClip): [number, number][] {
  const pts = new Set<number>([0, c.duration_ms]);
  if (c.fade_in_ms > 0) pts.add(c.fade_in_ms);
  if (c.fade_out_ms > 0) pts.add(c.duration_ms - c.fade_out_ms);
  const ducked = isDucked(p, c), iv = voiceIntervals(p.audio), d = p.ducking;
  if (ducked) {
    for (const [a, b] of iv) {
      const lo = a - d.attack_ms, hi = b + d.release_ms;
      for (const x of [lo, a, b, hi]) pts.add(x - c.start_ms);
      for (let x = lo; x <= hi; x += 40) pts.add(x - c.start_ms);
    }
  }
  return [...pts].filter((x) => x >= 0 && x <= c.duration_ms).sort((x, y) => x - y)
    .map((x): [number, number] => [x, gainAt(c, x) * (ducked ? duckGainAt(c.start_ms + x, iv, d.amount_db, d.attack_ms, d.release_ms) : 1)]);
}
